import React from 'react';
import { useWaveSurfer } from './useWaveSurfer';
import type { LibraryFile } from './types';
import { getApiUrl } from '../api';

type AudioPreviewProps = {
  file: LibraryFile;
  themeKey: string;
  isActive: boolean;
};

const AudioPreview = ({ file, themeKey, isActive }: AudioPreviewProps) => {
  const audioUrl = getApiUrl(
    `/api/stream?path=${encodeURIComponent(file.path)}`
  );
  const {
    waveformRef,
    isReady,
    isPlaying,
    error,
    playPause,
    setPlaybackRate,
    stop,
  } = useWaveSurfer(audioUrl, { height: 120 }, themeKey);
  const [pitchSemitones, setPitchSemitones] = React.useState(0);
  const [speedPercent, setSpeedPercent] = React.useState(100);

  React.useEffect(() => {
    const pitchFactor = Math.pow(2, pitchSemitones / 12);
    const speedFactor = speedPercent / 100;
    setPlaybackRate(pitchFactor * speedFactor);
  }, [pitchSemitones, speedPercent, setPlaybackRate]);

  React.useEffect(() => {
    setPitchSemitones(0);
    setSpeedPercent(100);
  }, [file.id]);

  React.useEffect(() => {
    if (!isActive) {
      stop();
    }
  }, [isActive, stop]);

  return (
    <div className="editor-body">
      <div className="waveform-shell">
        <button
          className="transport"
          type="button"
          onClick={playPause}
          disabled={!isReady || !!error}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="5" y="4" width="5" height="16" rx="1.5" />
              <rect x="14" y="4" width="5" height="16" rx="1.5" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <polygon points="7,5 19,12 7,19" />
            </svg>
          )}
        </button>
        <div className="waveform-stack">
          <div className="waveform" ref={waveformRef} />
        </div>
      </div>
      <div className="controls">
        <label>
          Pitch
          <input
            type="range"
            min="-12"
            max="12"
            step="1"
            value={pitchSemitones}
            onChange={(event) => setPitchSemitones(Number(event.target.value))}
          />
          <span className="control-value">
            {pitchSemitones > 0 ? '+' : ''}{pitchSemitones} st
          </span>
        </label>
        <label>
          Speed
          <input
            type="range"
            min="60"
            max="160"
            step="1"
            value={speedPercent}
            onChange={(event) => setSpeedPercent(Number(event.target.value))}
          />
          <span className="control-value">{speedPercent}%</span>
        </label>
      </div>
    </div>
  );
};

export default AudioPreview;
